/**
 * Functions for working with 2 dimensional line segments
 * @module
 */
import type { Slope } from "common/types/nominalNumbersTypes";
import { slopeNew } from "common/utils/nominalNumbers";
import { arc2ByThreePoints } from "./arc";
import { ANGLE_EPSILON, DIST_EPSILON, DIST_EPSILON_SQUARED, Line2CollinearOverlap, Line2Side } from "./core";
import { polyLine2New, type LineAnchor, type PolyLine2 } from "./polyLine2Types";
import { ray2PerpDistance, ray2Side } from "./ray2";
import type { Vec2 } from "./vec2";
import {
  vec2Sub,
  vec2Dist,
  vec2DistSquared,
  vec2Dot,
  vec2Lerp,
  vec2LenSquared,
  vec2Normalize,
  vec2Perp,
  vec2ProjectScaleFactor,
  vec2Result,
  vec2Scale,
  vec2Rotate,
  vec2Add,
  vec2FromAngle,
  vec2RotateAround,
} from "./vec2";

const { abs } = Math;

export const COLLINEAR_DISTANCE_TOL = 0.001;

export const line2Len = vec2Dist;

export type Line2Segment = [number, number, number, number]; // [x0, y0, x1, y1]

export function line2Eval(x0: number, y0: number, x1: number, y1: number, t: number, normalized: boolean) {
  if (normalized) {
    return vec2Lerp(x0, y0, x1, y1, t);
  }

  const [dirx, diry] = line2Tangent(x0, y0, x1, y1);

  vec2Result[0] = x0 + dirx * t;
  vec2Result[1] = y0 + diry * t;

  return vec2Result;
}

export function line2EvalBump(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  t: number,
  normalized: boolean,
  bump: number,
) {
  const [dirx, diry] = line2Tangent(x0, y0, x1, y1);

  if (normalized) {
    t *= line2Len(x0, y0, x1, y1);
  }

  t += bump;

  vec2Result[0] = x0 + dirx * t;
  vec2Result[1] = y0 + diry * t;

  return vec2Result;
}

export function line2EvalOffset(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  t: number,
  normalized: boolean,
  offset: number,
) {
  const [nx, ny] = line2Normal(x0, y0, x1, y1);
  const [x, y] = line2Eval(x0, y0, x1, y1, t, normalized);

  vec2Result[0] = x + nx * offset;
  vec2Result[1] = y + ny * offset;

  return vec2Result;
}

export function line2MidPt(x0: number, y0: number, x1: number, y1: number) {
  return line2Eval(x0, y0, x1, y1, 0.5, true);
}

export function line2Tangent(x0: number, y0: number, x1: number, y1: number, negate: boolean = false) {
  if (negate) {
    return vec2Normalize(x0 - x1, y0 - y1);
  } else {
    return vec2Normalize(x1 - x0, y1 - y0);
  }
}

// line2Normal provides the tangent of the line rotated by 90 degrees in the CW direction, CCW if negate is set to true
export function line2Normal(x0: number, y0: number, x1: number, y1: number, negate: boolean = false) {
  if (negate) {
    return vec2Normalize(y0 - y1, x1 - x0);
  } else {
    return vec2Normalize(y1 - y0, x0 - x1);
  }
}

export function line2NormalSafe(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  epsilonSquared: number = DIST_EPSILON_SQUARED,
): Vec2 | null {
  const dx = x1 - x0;
  const dy = y1 - y0;
  if (vec2LenSquared(dx, dy) < epsilonSquared) {
    return null;
  }
  return line2Normal(x0, y0, x1, y1, false);
}

export function line2IsParallel(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  epsilon: number = ANGLE_EPSILON,
): boolean {
  const [dx0, dy0] = line2Tangent(x1, y1, x0, y0);
  const [dx1, dy1] = line2Normal(x3, y3, x2, y2);

  return abs(vec2Dot(dx0, dy0, dx1, dy1)) < epsilon;
}

export function line2IsAxisAligned(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  epsilon: number = DIST_EPSILON,
): boolean {
  const [dirX, dirY] = line2Tangent(x0, y0, x1, y1);
  const dot = Math.abs(vec2Dot(dirX, dirY, 1, 0));
  return dot <= epsilon || dot >= 1 - epsilon;
}

export function line2IsHorizontal(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  epsilon: number = DIST_EPSILON,
): boolean {
  const [dirX, dirY] = line2Tangent(x0, y0, x1, y1);
  const dot = Math.abs(vec2Dot(dirX, dirY, 0, 1));
  return dot <= epsilon;
}

export function line2IsVertical(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  epsilon: number = DIST_EPSILON,
): boolean {
  const [dirX, dirY] = line2Tangent(x0, y0, x1, y1);
  const dot = Math.abs(vec2Dot(dirX, dirY, 1, 0));
  return dot <= epsilon;
}

/**
 * Canonicalize the tangent so that oppositely oriented tangents become the same
 * tangent
 */
export function line2CanonicalTangent(x0: number, y0: number, x1: number, y1: number): Vec2 {
  const [tx, ty] = line2Tangent(x0, y0, x1, y1);

  let absTangentX: number;
  let absTangentY: number;
  if (line2IsHorizontal(x0, y0, x1, y1)) {
    absTangentX = 1;
    absTangentY = 0;
  } else if (line2IsVertical(x0, y0, x1, y1)) {
    absTangentX = 0;
    absTangentY = 1;
  } else if (tx * ty > 0) {
    // quadrant 1 or 3 become quadrant 1
    absTangentX = Math.abs(tx);
    absTangentY = Math.abs(ty);
  } else {
    // quadrant 2 or 4 become quadrant 2
    absTangentX = -Math.abs(tx);
    absTangentY = Math.abs(ty);
  }

  vec2Result[0] = absTangentX;
  vec2Result[1] = absTangentY;

  return vec2Result;
}

export function line2PerpDistance(x0: number, y0: number, x1: number, y1: number, x: number, y: number): number {
  const [dirx, diry] = line2Tangent(x0, y0, x1, y1);
  return ray2PerpDistance(x0, y0, dirx, diry, x, y);
}

export function line2Side(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  angleTol: number = DIST_EPSILON,
  distanceSquaredTol: number = DIST_EPSILON_SQUARED,
): Line2Side {
  // if any point neighbors are concident, categorize as collinear
  if (vec2DistSquared(x0, y0, x1, y1) < distanceSquaredTol || vec2DistSquared(x1, y1, x2, y2) < distanceSquaredTol) {
    return Line2Side.COLLINEAR;
  }

  const [dx0, dy0] = line2Tangent(x1, y1, x0, y0);
  const [dx1, dy1] = line2Tangent(x2, y2, x1, y1);

  const [perpx, perpy] = vec2Perp(dx0, dy0);

  const dot = vec2Dot(perpx, perpy, dx1, dy1);

  if (abs(dot) < angleTol) {
    return Line2Side.COLLINEAR;
  } else if (dot < angleTol) {
    return Line2Side.LEFT;
  } else {
    return Line2Side.RIGHT;
  }
}

export function line2Vec(line: number[]): number[] {
  const x = line[2] - line[0];
  const y = line[3] - line[1];

  return vec2Normalize(x, y);
}

export function line2ProjectPt(x0: number, y0: number, x1: number, y1: number, x: number, y: number): Vec2 {
  const [dx, dy] = vec2Sub(x, y, x0, y0);
  const [dx2, dy2] = vec2Sub(x1, y1, x0, y0);
  const [nx, ny] = vec2Normalize(dx2, dy2);
  const dot = vec2Dot(dx, dy, nx, ny);
  const [scaledX, scaledY] = vec2Scale(nx, ny, dot);
  return [x0 + scaledX, y0 + scaledY];
}

/**
 * Computes the normalized parameter t for projecting point (x, y) onto the line segment from (x0, y0) to (x1, y1).
 * Returns t where:
 *   t = 0 corresponds to (x0, y0)
 *   t = 1 corresponds to (x1, y1)
 *   t < 0 means the projection is before the start point
 *   t > 1 means the projection is beyond the end point
 * Returns undefined if the segment is degenerate (endpoints are coincident).
 */
export function line2Parameter(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  x: number,
  y: number,
): number | undefined {
  if (vec2DistSquared(x0, y0, x1, y1) < DIST_EPSILON_SQUARED) {
    return undefined;
  }
  return vec2ProjectScaleFactor(x - x0, y - y0, x1 - x0, y1 - y0);
}

export function line2Collinear(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  angleTol: number = 0.0001,
  distanceTol: number = COLLINEAR_DISTANCE_TOL,
): boolean {
  // This formulation consistently handles the case where lines are parallel to limits of numerical accuracy but there's a small gap between them.
  // The points that are selected along the line can still have an effect on whether they're detected as collinear in case the lines are not perfectly parallel.
  const [dx0, dy0] = line2Tangent(x1, y1, x0, y0);
  const [dx1, dy1] = line2Normal(x3, y3, x2, y2);

  return (
    abs(vec2Dot(dx0, dy0, dx1, dy1)) < angleTol &&
    ray2PerpDistance(x0, y0, dx0, dy0, (x2 + x3) * 0.5, (y2 + y3) * 0.5) < distanceTol
  );
}

export function line2IsDirectedCoincident(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  distanceTol: number = DIST_EPSILON,
): boolean {
  return (
    Math.abs(x0 - x2) < distanceTol &&
    Math.abs(y0 - y2) < distanceTol &&
    Math.abs(x1 - x3) < distanceTol &&
    Math.abs(y1 - y3) < distanceTol
  );
}

export function line2IsCoincident(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  distanceTol: number = DIST_EPSILON,
): boolean {
  return (
    line2IsDirectedCoincident(x0, y0, x1, y1, x2, y2, x3, y3, distanceTol) ||
    line2IsDirectedCoincident(x0, y0, x1, y1, x3, y3, x2, y2, distanceTol)
  );
}

export function line2CollinearOverlap(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  x4: number,
  y4: number,
  isEndInclusive: boolean = true,
  endCoincidenceTolSqrd: number = DIST_EPSILON_SQUARED,
  parallelLineTol: number = DIST_EPSILON,
): Line2CollinearOverlap {
  const dist: number[] = [0, 0, 0, 0];
  dist[0] = vec2DistSquared(x1, y1, x3, y3);
  dist[1] = vec2DistSquared(x1, y1, x4, y4);
  dist[2] = vec2DistSquared(x2, y2, x3, y3);
  dist[3] = vec2DistSquared(x2, y2, x4, y4);

  if (
    (dist[0] < endCoincidenceTolSqrd && dist[3] < endCoincidenceTolSqrd) ||
    (dist[1] < endCoincidenceTolSqrd && dist[2] < endCoincidenceTolSqrd)
  ) {
    return Line2CollinearOverlap.FULL;
  }

  // Check for collinearity
  if (!line2Collinear(x1, y1, x2, y2, x3, y3, x4, y4)) {
    return Line2CollinearOverlap.NONE;
  }

  // Check if touching
  if (isEndInclusive && dist.filter((pt) => pt < endCoincidenceTolSqrd).length !== 0) {
    return Line2CollinearOverlap.POINT;
  }

  // Check for overlap
  const xMinLine1 = x1 < x2 ? x1 : x2;
  const xMaxLine1 = x1 > x2 ? x1 : x2;
  const xMinLine2 = x3 < x4 ? x3 : x4;
  const xMaxLine2 = x3 > x4 ? x3 : x4;
  const yMinLine1 = y1 < y2 ? y1 : y2;
  const yMaxLine1 = y1 > y2 ? y1 : y2;
  const yMinLine2 = y3 < y4 ? y3 : y4;
  const yMaxLine2 = y3 > y4 ? y3 : y4;
  let overlapTypeX: Line2CollinearOverlap = Line2CollinearOverlap.NONE;
  let overlapTypeY: Line2CollinearOverlap = Line2CollinearOverlap.NONE;

  if (xMinLine1 < xMinLine2 && xMinLine2 < xMaxLine1) {
    if (xMaxLine1 < xMaxLine2) {
      overlapTypeX = Line2CollinearOverlap.PARTIAL;
    } else {
      overlapTypeX = Line2CollinearOverlap.LINE2_INSIDE_LINE1;
    }
  } else if (xMinLine1 > xMinLine2 && xMinLine1 < xMaxLine2) {
    if (xMaxLine1 > xMaxLine2) {
      overlapTypeX = Line2CollinearOverlap.PARTIAL;
    } else {
      overlapTypeX = Line2CollinearOverlap.LINE1_INSIDE_LINE2;
    }
  }

  if (yMinLine1 < yMinLine2 && yMinLine2 < yMaxLine1) {
    if (yMaxLine1 < yMaxLine2) {
      overlapTypeY = Line2CollinearOverlap.PARTIAL;
    } else {
      overlapTypeY = Line2CollinearOverlap.LINE2_INSIDE_LINE1;
    }
  } else if (yMinLine1 > yMinLine2 && yMinLine1 < yMaxLine2) {
    if (yMaxLine1 > yMaxLine2) {
      overlapTypeY = Line2CollinearOverlap.PARTIAL;
    } else {
      overlapTypeY = Line2CollinearOverlap.LINE1_INSIDE_LINE2;
    }
  }

  if (Math.abs(x1 - x2) < parallelLineTol) {
    return overlapTypeY;
  } else if (Math.abs(y1 - y2) < parallelLineTol) {
    return overlapTypeX;
  } else if (overlapTypeX === overlapTypeY) {
    return overlapTypeX;
  }

  return Line2CollinearOverlap.NONE;
}

interface Line2CollinearOverlapNone {
  readonly classification: Line2CollinearOverlap.NONE;
}

interface Line2CollinearOverlapPoint {
  readonly classification: Line2CollinearOverlap.POINT;
  /** The normalized parameter value of the overlap point on the first line. */
  readonly t: number;
}

interface Line2CollinearOverlapSegment {
  readonly classification:
    | Line2CollinearOverlap.FULL
    | Line2CollinearOverlap.LINE1_INSIDE_LINE2
    | Line2CollinearOverlap.LINE2_INSIDE_LINE1
    | Line2CollinearOverlap.PARTIAL;
  /** The normalized parameter value of the overlap start on the first line. */
  readonly t0: number;
  /** The normalized parameter value of the overlap end on the first line. */
  readonly t1: number;
}

export type Line2CollinearOverlapResult =
  | Line2CollinearOverlapNone
  | Line2CollinearOverlapPoint
  | Line2CollinearOverlapSegment;

/**
 * Classifies the projected overlap between collinear segments.
 */
export function line2CollinearOverlapSegment(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  x4: number,
  y4: number,
  distanceTol = COLLINEAR_DISTANCE_TOL,
  angleTol?: number,
): Line2CollinearOverlapResult {
  const distanceTolSqrd = distanceTol * distanceTol;
  const line1LengthSqrd = vec2DistSquared(x1, y1, x2, y2);
  if (line1LengthSqrd < distanceTolSqrd || vec2DistSquared(x3, y3, x4, y4) < distanceTolSqrd) {
    return { classification: Line2CollinearOverlap.NONE };
  }

  if (
    (vec2DistSquared(x1, y1, x3, y3) < distanceTolSqrd && vec2DistSquared(x2, y2, x4, y4) < distanceTolSqrd) ||
    (vec2DistSquared(x1, y1, x4, y4) < distanceTolSqrd && vec2DistSquared(x2, y2, x3, y3) < distanceTolSqrd)
  ) {
    return { classification: Line2CollinearOverlap.FULL, t0: 0, t1: 1 };
  }

  if (!line2Collinear(x1, y1, x2, y2, x3, y3, x4, y4, angleTol, distanceTol)) {
    return { classification: Line2CollinearOverlap.NONE };
  }

  const t3 = line2Parameter(x1, y1, x2, y2, x3, y3);
  const t4 = line2Parameter(x1, y1, x2, y2, x4, y4);
  if (t3 === undefined || t4 === undefined) {
    return { classification: Line2CollinearOverlap.NONE };
  }

  const line2MinT = Math.min(t3, t4);
  const line2MaxT = Math.max(t3, t4);
  const overlapStart = Math.max(0, line2MinT);
  const overlapEnd = Math.min(1, line2MaxT);

  const paramDistanceWithinTol = (distance: number): boolean =>
    distance * distance * line1LengthSqrd <= distanceTolSqrd;

  const clampOverlapParameter = (t: number): number => {
    if (paramDistanceWithinTol(t)) {
      return 0;
    }

    if (paramDistanceWithinTol(t - 1)) {
      return 1;
    }

    if (t < 0) {
      return 0;
    }

    if (t > 1) {
      return 1;
    }

    return t;
  };

  const overlapTLength = overlapEnd - overlapStart;

  if (overlapTLength < 0) {
    if (!paramDistanceWithinTol(overlapTLength)) {
      return { classification: Line2CollinearOverlap.NONE };
    }

    const pointT = (overlapStart + overlapEnd) * 0.5;
    return { classification: Line2CollinearOverlap.POINT, t: clampOverlapParameter(pointT) };
  }

  if (paramDistanceWithinTol(overlapTLength)) {
    const pointT = (overlapStart + overlapEnd) * 0.5;
    return { classification: Line2CollinearOverlap.POINT, t: clampOverlapParameter(pointT) };
  }

  const line1InsideLine2 = line2MinT <= 0 || paramDistanceWithinTol(line2MinT);
  const line1InsideLine2End = line2MaxT >= 1 || paramDistanceWithinTol(line2MaxT - 1);
  const line2InsideLine1 = line2MinT >= 0 || paramDistanceWithinTol(line2MinT);
  const line2InsideLine1End = line2MaxT <= 1 || paramDistanceWithinTol(line2MaxT - 1);
  const t0 = clampOverlapParameter(overlapStart);
  const t1 = clampOverlapParameter(overlapEnd);

  if (line1InsideLine2 && line1InsideLine2End && line2InsideLine1 && line2InsideLine1End) {
    return { classification: Line2CollinearOverlap.FULL, t0, t1 };
  } else if (line1InsideLine2 && line1InsideLine2End) {
    return {
      classification: Line2CollinearOverlap.LINE1_INSIDE_LINE2,
      t0,
      t1,
    };
  } else if (line2InsideLine1 && line2InsideLine1End) {
    return {
      classification: Line2CollinearOverlap.LINE2_INSIDE_LINE1,
      t0,
      t1,
    };
  }

  return { classification: Line2CollinearOverlap.PARTIAL, t0, t1 };
}

export function line2AnchorRemap(x0: number, y0: number, x1: number, y1: number, anchor: LineAnchor): Vec2 {
  if (anchor === "CENTER") {
    [x0, y0] = line2MidPt(x0, y0, x1, y1);
  } else if (anchor === "END") {
    [x0, y0, x1, y1] = [x1, y1, x0, y0];
  }

  return [x0, y0, x1, y1];
}

/**
 * This function computes vec points at the offset side and distance defined by point x,y for a line segment [x0,y0]-[x1,y1]
 */
export function line2EndOffsetPts(x0: number, y0: number, x1: number, y1: number, x: number, y: number): [Vec2, Vec2] {
  const [vX, vY] = vec2Sub(x1, y1, x0, y0);
  const [normX, normY] = vec2Normalize(vX, vY);
  const side = ray2Side(x0, y0, normX, normY, x, y);

  if (side === Line2Side.COLLINEAR) {
    return [[x0, y0] as Vec2, [x1, y1] as Vec2];
  }
  const perpDist = ray2PerpDistance(x0, y0, normX, normY, x, y);
  const [scaledX, scaledY] = vec2Scale(normX, normY, perpDist);

  const rotationAngle = side === Line2Side.LEFT ? Math.PI / 2 : -Math.PI / 2;

  const [rotateX, rotateY] = vec2Rotate(scaledX, scaledY, rotationAngle);
  const [startX, startY] = vec2Add(rotateX, rotateY, x0, y0);
  const [endX, endY] = vec2Add(rotateX, rotateY, x1, y1);
  return [[startX, startY] as Vec2, [endX, endY] as Vec2];
}

export function line2ThickenRadius(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  radius: number,
  numSides: number = 3,
): PolyLine2 {
  const [nx, ny] = line2Normal(x0, y0, x1, y1);
  const [nrx, nry] = vec2Scale(nx, ny, radius);
  const [vx, vy] = line2Vec([x0, y0, x1, y1]);
  const [lx, ly] = vec2Normalize(vx, vy);
  const [lrx, lry] = vec2Scale(lx, ly, radius);

  /**
   *     .--e-------------d--.
   *    |   :             :   |
   *    f - 0-----/-------1 - c
   *    |   :     :r      :   |
   *     '--a-----/-------b--'
   */
  const [ax, ay] = vec2Add(x0, y0, nrx, nry);
  const [bx, by] = vec2Add(x1, y1, nrx, nry);
  const [cx, cy] = vec2Add(x1, y1, lrx, lry);
  const [dx, dy] = vec2Add(x1, y1, -nrx, -nry);
  const [ex, ey] = vec2Add(x0, y0, -nrx, -nry);
  const [fx, fy] = vec2Add(x0, y0, -lrx, -lry);

  const bcd = arc2ByThreePoints(bx, by, cx, cy, dx, dy, undefined, numSides);
  const efa = arc2ByThreePoints(ex, ey, fx, fy, ax, ay, undefined, numSides);

  return polyLine2New([ax, ay, ...bcd, ...efa]);
}

export function line2Slope(x0: number, y0: number, x1: number, y1: number, epsilon: number = DIST_EPSILON): Slope {
  const [dx, dy] = vec2Sub(x1, y1, x0, y0);
  if (Math.abs(dx) < epsilon) {
    return dy > 0 ? slopeNew(Infinity) : slopeNew(-Infinity);
  }
  return slopeNew(dy / dx);
}

/**
 * Calculate the domain of a line segment in 1D space
 * relative to the projected origin.
 * Optionally the origin can be specified.
 */
export function line2Domain(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  oX: number = 0,
  oY: number = 0,
): [number, number] {
  // project origin to line
  let [projX, projY] = line2ProjectPt(x0, y0, x1, y1, oX, oY);

  // distance from projected origin to line segment endpoints
  // these values are always positive
  let distStr = vec2Dist(x0, y0, projX, projY);
  let distEnd = vec2Dist(x1, y1, projX, projY);

  // normal of line from origin to projected origin
  let [projXN, projYN] = line2Normal(oX, oY, projX, projY);

  // if the projected origin is very close to the origin (resulting in a zero
  // length normal), use the line segment normal instead
  if (line2Len(oX, oY, projX, projY) < DIST_EPSILON) {
    [projXN, projYN] = line2Normal(x0, y0, x1, y1);
    // ensure normal is pointing in the correct direction
    if (projXN < -DIST_EPSILON || (Math.abs(projXN) < DIST_EPSILON && projYN < -DIST_EPSILON)) {
      [projXN, projYN] = [-projXN, -projYN];
    }
    // create a new dummy origin
    oX = oX - projXN;
    oY = oY - projYN;
  }

  if (projY < oY - DIST_EPSILON || (Math.abs(projY - oY) < DIST_EPSILON && projX < oX - DIST_EPSILON)) {
    // swap origin and projected origin
    [oX, oY, projX, projY] = [projX, projY, oX, oY];
    // reverse normal
    [projXN, projYN] = [-projXN, -projYN];
  }

  // determine quadrant of point relative to projected origin
  const sideStr = line2Side(oX, oY, projX, projY, x0, y0);
  const sideEnd = line2Side(oX, oY, projX, projY, x1, y1);

  if (sideStr === Line2Side.LEFT) {
    // line is in quadrant 1 or 3
    distStr = -distStr;
  }
  if (sideEnd === Line2Side.LEFT) {
    // line is in quadrant 1 or 3
    distEnd = -distEnd;
  }

  return [distStr, distEnd];
}

/**
 * Evaluate a domain on a line as a line segment
 * assumes the given point is the projected origin of the line
 * @param x projected origin x coordinate
 * @param y projected origin y coordinate
 * @param slope slope of the line
 * @param domain domain of the line segment in 1D space
 */
export function line2SegmentFromPointSlopeDomain(
  x: number,
  y: number = 0,
  slope: Slope,
  [d0, d1]: [number, number],
): Line2Segment {
  let dirX: number;
  let dirY: number;

  if (slope === Infinity) {
    dirX = 0;
    dirY = 1;
  } else if (slope === -Infinity) {
    dirX = 0;
    dirY = -1;
  } else {
    const len = Math.sqrt(1 + (slope as number) * (slope as number));
    dirX = 1 / len;
    dirY = (slope as number) / len;
  }

  return [x + dirX * d0, y + dirY * d0, x + dirX * d1, y + dirY * d1];
}

export function line2FromPointAngleDomain(
  x: number,
  y: number,
  angle: number,
  [d0, d1]: [number, number],
): Line2Segment {
  const [rx0, ry0] = vec2RotateAround(x + d0, y, angle, x, y);
  const [rx1, ry1] = vec2RotateAround(x + d1, y, angle, x, y);
  return [rx0, ry0, rx1, ry1];
}

/**
 * Create a line segment from a distance, angle, and domain
 * @param angle angle of the line segment relative to the x axis
 * @param distance perpendicular distance from the origin to the line segment
 * @param domain domain of the line segment in 1D space relative to the projected origin
 * @returns
 */
export function line2FromDistanceAngleDomain(angle: number, distance: number, domain: [number, number]): Line2Segment {
  const perpAngle = angle - Math.PI / 2;
  const [dx, dy] = vec2FromAngle(perpAngle);
  const [sx, sy] = vec2Scale(dx, dy, distance);
  return line2FromPointAngleDomain(sx, sy, angle, domain);
}

export function line2TranslateCopy(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  dx: number,
  dy: number,
): Line2Segment {
  return [x0 + dx, y0 + dy, x1 + dx, y1 + dy];
}

export function line2sTranslateCopy(lines: Line2Segment[], [dx, dy]: Vec2): Line2Segment[] {
  return lines.map(([x0, y0, x1, y1]) => line2TranslateCopy(x0, y0, x1, y1, dx, dy));
}
